import { Box, Button } from "@mui/material";
import { AddressSearchAutocomplete } from "Components/Shared/Inputs/Form/AddressSearchAutocomplete";
import { useReportAddressUncommitted } from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import type { AddressSuggestionDto } from "Infrastructure/Api/Api";
import { type ReactNode, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import type { FormattableAddress } from "Utils/AddressUtils";

const RESOURCES = Resources.Components.Shared.AddressInput;

type Props = {
  /** Current address (OCR / BE / prior pick), shown in the search input */
  address?: FormattableAddress | null;
  /** Maps a picked suggestion onto the parent form's detail fields. */
  onAcceptSuggestion: (suggestion: AddressSuggestionDto) => void;
  /** Clears the parent form's detail fields when the search input is cleared. */
  onClear?: () => void;
  /** Whether the address detail fields currently have validation errors. In autocomplete mode this puts the search input into an error state. */
  hasErrors?: boolean;
  /** Detail inputs (street, number, PSČ, …). Always mounted; hidden in autocomplete mode. */
  children: ReactNode;
  searchLabel?: string;
  /** Label of the toggle while in autocomplete mode (switches to manual). */
  manualToggleLabel?: string;
  /** Label of the toggle while in manual mode (switches back to search). */
  searchToggleLabel?: string;
  isSearchDisabled?: boolean;
};

/**
 * Two-mode address entry shared across all address forms (ZS-1495).
 *
 * - **Autocomplete mode (default):** a single registry-search input that shows
 *   the formatted address and populates the hidden detail fields behind it.
 * - **Manual mode:** the search input is hidden and the detail fields are shown
 *   for free-form entry.
 *
 * The detail fields (`children`) are the single source of truth and stay
 * mounted in both modes, so toggling never loses data.
 */
export const AddressInput = ({
  address,
  onAcceptSuggestion,
  onClear,
  hasErrors,
  children,
  searchLabel,
  manualToggleLabel,
  searchToggleLabel,
  isSearchDisabled,
}: Props) => {
  const [isManualMode, setIsManualMode] = useState(false);
  const reportUncommitted = useReportAddressUncommitted();
  const { t } = useResource();

  const handleToggleMode = () => {
    const nextManualMode = !isManualMode;
    setIsManualMode(nextManualMode);
    // Manual mode has no search box, so there's never an unapplied query there.
    if (nextManualMode) {
      reportUncommitted(false);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Sits on the same line as the field's label (top-left) in both modes. */}
      <Button
        variant="text"
        size="small"
        onClick={handleToggleMode}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          height: "21px", //matching label's height
          textTransform: "none",
        }}
      >
        {isManualMode
          ? (searchToggleLabel ?? t(RESOURCES.SearchInRegistry))
          : (manualToggleLabel ?? t(RESOURCES.EnterManually))}
      </Button>
      {!isManualMode && (
        <AddressSearchAutocomplete
          label={searchLabel ?? t(RESOURCES.SearchLabel)}
          address={address}
          onAcceptSuggestion={onAcceptSuggestion}
          onClear={onClear}
          hasErrors={hasErrors}
          onUncommittedChange={reportUncommitted}
          disabled={isSearchDisabled}
        />
      )}
      <Box sx={{ display: isManualMode ? "contents" : "none" }}>{children}</Box>
    </Box>
  );
};
